<template>
  <div class="w-full">
    <div class="inline-flex items-center mb-[8px]">
      <div class="text-[#003F82] text-[16px] leading-[140%] font-bold">
        {{ headerText }}
      </div>
      <div v-if="modalInformation" class="ml-[10px]" data-testid="question-mark-icon" @click="showModal">
        <QuestionMarkIcon />
      </div>
    </div>
    <div class="mt-[8px]">
      <VStack gap="2">
        <template v-for="(item, idx) in items">
          <CheckBoxButton
            :key="idx"
            :text="item.text"
            :selected="item.selected"
            :disabled="item.disabled"
            @choice-selected="handleChoiceSelected(idx)"
          >
            <template v-slot:extra-column>
              <div v-if="item.tagInformation" class="appearence-none">
                <LabelTag :color="item.tagInformation.color">
                  {{ item.tagInformation.text }}
                </LabelTag>
              </div>
              <div v-else-if="item.description" class="text-[13px] leading-[120%]">
                {{ item.description }}
              </div>
            </template>
            <template v-slot:extra-row>
              <div v-if="item.description && item.tagInformation" class="appearence-none">
                <div class="w-0 mt-[10px]" />
                <div class="text-[13px] leading-[120%]">
                  {{ item.description }}
                </div>
              </div>
            </template>
          </CheckBoxButton>
        </template>
      </VStack>
    </div>

    <InformationModal v-if="modalInformation" v-bind="modalProps" @close="hideModal">
      <slot name="modal-content" />
    </InformationModal>
  </div>
</template>

<script setup lang="ts">
import { ref, toRef, computed } from "vue";
import InformationModal from "@/ProductPage/components/InformationModal";
import CheckBoxButton from "@/ProductPage/components/CheckBoxButton";
import { VStack } from "@/Layout/Stack";
import QuestionMarkIcon from "@stylesheets/assets/question_mark.svg?inline";
import LabelTag, { LabelTagColor } from "@/ProductPage/components/LabelTag";

interface Choice {
  id: string;
  text: string;
  description?: string;
  tagInformation?: {
    color: LabelTagColor;
    text: string;
  };
  selected: boolean;
  disabled: boolean;
}

interface ModalInformation {
  title: string;
  description?: string;
}

const props = defineProps<{
  headerText: string;
  choices: Array<Choice>;
  modalInformation?: ModalInformation;
}>();

const emit = defineEmits(["item-selected"]);

const items = toRef(props, "choices");

const modalVisible = ref(false);

const modalProps = computed(() => {
  return {
    ...props.modalInformation,
    visible: modalVisible.value,
  };
});

function showModal(): void {
  if (props.modalInformation) {
    modalVisible.value = true;
  }
}

function hideModal(): void {
  if (props.modalInformation) {
    modalVisible.value = false;
  }
}

function handleChoiceSelected(idx: number) {
  items.value.forEach((item, index) => {
    if (index === idx) {
      item.selected = true;
      emit("item-selected", { id: item.id });
    } else {
      item.selected = false;
    }
  });
}
</script>

<style scoped>
@tailwind utilities;
</style>
